/**
 * 💡 가격 참고 창 — 견적서 작성 창의 품목 줄에서 연다 (v2.235.0 · 2026-10-09)
 *
 * 사용자: 「견적서 작성할 때 제안가격 참고하려고 하는 건데, 더 편의성 제공할 수 있는 게 없을지」.
 * 운영 자료(10-09): 견적 115건 중 품목 1개짜리 69% · 87% 가 영업건 상세에서 출발 → 원가분석표를 따로 만들지 않고도
 *   견적 줄에서 바로 네 곳을 보고 단가를 정하게 한다.
 *
 * 네 탭
 *   📒 주요 가격표  — 공급단가·소비자가 → [이 단가로] 그대로 단가에(품목명·규격이 비었으면 같이 채움)
 *   🧾 매입가       — 품목 마스터에서 찾아 최근/평균 매입가·이력 → 「원가」로 고름
 *   🟣 인포트렌드   — 현재 분기 가격표 공급가 → 「원가」로 고름
 *   📄 지난 견적    — 같은 품목을 지난번엔 얼마에 냈나(날짜·받는 곳·규격 함께) → [이 단가로]
 * 원가를 고르면 아래 「제안 단가 계산」 띠가 원가 ÷ (1 − 마진율) 올림(원가분석표와 같은 식)으로 제안 단가를 만든다 → [단가에 넣기].
 *   그때 원가(costUnit)도 함께 넘겨 작성 창이 그 줄의 「원가 · 마진율」을 보여 준다.
 * 마진율·올림 단위는 이 브라우저에 기억한다(localStorage · 못 읽어도 기본값 15% · 1,000원).
 * ⚠공용 DraggableModal — 입력 창이라 배경 클릭·ESC 로 닫히지 않는다(H-4). 견적서 <form> 안에 그려지므로 모든 단추는 type="button"
 *   (Enter 는 작성 창의 blockEnterSubmit 이 막는다).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MainProductRow } from '../../lib/price-list-api';
import { searchQuoteItemPrices } from '../../lib/quote-api';
import { MARGIN_MAX, ROUND_UNITS, appliedUnitCost, autoSellUnit, fmtKrw } from '../../lib/cost-sheet-calc';
import { DraggableModal } from '../DraggableModal';
import { SegmentedControl } from '../ui/SegmentedControl';
import { NumCell } from '../cost-sheet/CostUi';
import { InfortrendPicker, PurchasePicker, matchesAllTokens } from '../cost-sheet/LinePickers';

export interface PriceRefApply {
  unitPrice: number;
  /** 원가 단가 — 원가에서 계산한 제안 단가일 때만(가격표·지난 견적은 null) */
  costUnit: number | null;
  /** 품목명·규격 — 받는 쪽은 비어 있을 때만 채운다 */
  itemName?: string;
  description?: string;
}

type Tab = 'main' | 'purchase' | 'infortrend' | 'quotes';
const TAB_LABEL: Record<Tab, string> = { main: '📒 주요 가격표', purchase: '🧾 매입가', infortrend: '🟣 인포트렌드 가격표', quotes: '📄 지난 견적' };

const PREF_MARGIN = 'quote.priceRef.marginPct';
const PREF_ROUND = 'quote.priceRef.roundUnit';
function readPref(key: string, fallback: number): number {
  try {
    const v = Number(window.localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}
function writePref(key: string, v: number): void {
  try { window.localStorage.setItem(key, String(v)); } catch { /* 사생활 모드 등 — 기억만 못 할 뿐 */ }
}

export function PriceReferenceModal({ itemName, description, currentUnitPrice, products, onApply, onClose }: {
  itemName: string;
  description: string;
  currentUnitPrice: number;
  products: MainProductRow[];
  onApply: (r: PriceRefApply) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('main');
  const [cost, setCost] = useState<{ unit: number; label: string; name: string; spec: string } | null>(null);
  const [marginPct, setMarginPct] = useState(() => Math.min(MARGIN_MAX, readPref(PREF_MARGIN, 15)));
  const [roundUnit, setRoundUnit] = useState(() => {
    const v = readPref(PREF_ROUND, 1000);
    return (ROUND_UNITS as readonly number[]).includes(v) ? v : 1000;
  });
  useEffect(() => { writePref(PREF_MARGIN, marginPct); }, [marginPct]);
  useEffect(() => { writePref(PREF_ROUND, roundUnit); }, [roundUnit]);

  const sell = cost ? autoSellUnit(cost.unit, marginPct, roundUnit) : 0;
  const applyComputed = (): void => {
    if (!cost) return;
    onApply({ unitPrice: sell, costUnit: cost.unit, itemName: cost.name, description: cost.spec });
  };

  return (
    <DraggableModal onClose={onClose} initialWidth={1040} initialHeight={740} zIndex={60} bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col">
      <div className="dm-drag-handle flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-white cursor-move select-none flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="ui-subsection-title">💡 가격 참고 <span className="text-sm font-normal text-slate-500 ml-1">{itemName.trim() || '(품목명 없음)'}</span></h3>
          <SegmentedControl ariaLabel="가격 참고 종류" value={tab} onChange={setTab}
            options={(Object.keys(TAB_LABEL) as Tab[]).map((t) => ({ key: t, label: TAB_LABEL[t] }))} />
        </div>
        <button type="button" onClick={onClose} className="ui-close" title="닫기">✕</button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-5">
        {tab === 'main' && <MainPriceTab itemName={itemName} description={description} products={products} onApply={onApply} />}
        {tab === 'purchase' && (
          <PurchasePicker initialQuery={itemName} showQty={false} addLabel="이 원가로 제안 단가 계산 ↓"
            onAdd={(l) => setCost({
              unit: appliedUnitCost(l),
              label: `매입 · ${l.name}${l.vendor ? ` · ${l.vendor}` : ''}${l.basis === 'recent' ? ' · 최근 매입가' : l.basis === 'avg' ? ' · 평균 매입가' : ''}`,
              name: l.name, spec: l.spec,
            })} />
        )}
        {tab === 'infortrend' && (
          <InfortrendPicker initialQuery={itemName} showQty={false} addLabel="이 공급가로 제안 단가 계산 ↓"
            onAdd={(l) => setCost({ unit: l.unitCost, label: `인포트렌드 ${l.quarter ?? ''} 공급가 · ${l.name}`, name: l.name, spec: l.spec })} />
        )}
        {tab === 'quotes' && <PastQuotesTab itemName={itemName} onApply={onApply} />}
      </div>

      {/* 제안 단가 계산 띠 — 원가분석표와 같은 식 */}
      <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 flex items-center gap-x-4 gap-y-2 flex-wrap">
        <div className="text-sm text-slate-800 min-w-0">
          원가 <b className="tabular-nums">{cost ? fmtKrw(cost.unit) : '—'}</b>
          <span className="block text-xs text-slate-500 truncate max-w-[22rem]" title={cost?.label ?? ''}>{cost?.label ?? '매입가·인포트렌드 탭에서 고르면 여기 들어옵니다'}</span>
        </div>
        <label className="ui-label inline-flex items-center gap-1">× 마진율
          <NumCell value={marginPct} step="any" className="w-20" onChange={(n) => setMarginPct(Math.min(MARGIN_MAX, n))} title={`판매가 기준 0 ~ ${MARGIN_MAX}%`} /> %
        </label>
        <label className="ui-label inline-flex items-center gap-1">올림
          <select value={roundUnit} onChange={(e) => setRoundUnit(Number(e.target.value))} className="ui-input ui-input-sm">
            {ROUND_UNITS.map((u) => <option key={u} value={u}>{u.toLocaleString('ko-KR')}원</option>)}
          </select>
        </label>
        <div className="text-sm text-slate-800">→ 제안 단가 <b className="text-lg tabular-nums text-emerald-700">{cost ? fmtKrw(sell) : '—'}</b></div>
        {currentUnitPrice > 0 && <div className="text-xs text-slate-500">지금 단가 {fmtKrw(currentUnitPrice)}</div>}
        <button type="button" onClick={applyComputed} disabled={!cost || sell <= 0}
          className="btn btn-success-solid ml-auto">
          단가에 넣기
        </button>
      </div>
    </DraggableModal>
  );
}

/* ── 📒 주요 가격표 — 작성 창이 이미 들고 있는 목록을 품목명·규격 글자로 좁혀 보인다 ── */
function MainPriceTab({ itemName, description, products, onApply }: { itemName: string; description: string; products: MainProductRow[]; onApply: (r: PriceRefApply) => void }) {
  const [q, setQ] = useState(itemName.trim());
  const rows = useMemo(() => {
    const term = q.trim();
    const hit = term
      ? products.filter((p) => matchesAllTokens(`${p.name} ${p.description} ${p.note ?? ''}`, term))
      : products;
    // 아무것도 안 걸리면 규격 첫 단어로 한 번 더(「HDD」 로 적고 규격에 「16TB」 를 쓴 경우)
    if (hit.length === 0 && term && description.trim()) {
      const w = description.trim().split(/\s+/)[0] ?? '';
      if (w) return products.filter((p) => matchesAllTokens(`${p.name} ${p.description} ${p.note ?? ''}`, w)).slice(0, 30);
    }
    return hit.slice(0, 30);
  }, [products, q, description]);
  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 품목 · 설명 · 비고 (띄어쓰기로 여러 글자 · 비우면 전부)"
        className="ui-input w-full mb-2" />
      {products.length === 0 ? (
        <div className="px-3 py-8 text-sm text-slate-400 text-center">주요 가격표가 비어 있습니다 — 가격표 메뉴에서 먼저 등록하세요</div>
      ) : rows.length === 0 ? (
        <div className="px-3 py-8 text-sm text-slate-400 text-center">맞는 품목이 없습니다 — 글자를 줄여 보세요</div>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr><th className="px-2 py-1.5 text-left w-20">구분</th><th className="px-2 py-1.5 text-left">품목</th><th className="px-2 py-1.5 text-left">설명</th><th className="px-2 py-1.5 text-right w-28">공급단가</th><th className="px-2 py-1.5 text-right w-28">소비자가</th><th className="px-2 py-1.5 w-24"></th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{p.category}</td>
                  <td className="px-2 py-1.5 font-medium text-slate-800">{p.name}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-pre-wrap line-clamp-2" title={p.description}>{p.description}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{fmtKrw(p.supplyPrice)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{fmtKrw(p.retailPrice)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button type="button" onClick={() => onApply({ unitPrice: Number(p.supplyPrice) || 0, costUnit: null, itemName: p.name, description: p.description })}
                      className="btn btn-success btn-sm">이 단가로</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-slate-500 mt-1.5">공급단가가 그대로 단가에 들어갑니다(부가세 별도). 품목명·규격이 비어 있으면 가격표 글자로 함께 채웁니다.</div>
    </div>
  );
}

/* ── 📄 지난 견적 — 같은 품목을 지난번엔 얼마에 냈나 (서버 /quotes/item-prices · 단가 0 줄 제외 · 최근 순) ── */
function PastQuotesTab({ itemName, onApply }: { itemName: string; onApply: (r: PriceRefApply) => void }) {
  const [q, setQ] = useState(itemName.trim());
  const [debounced, setDebounced] = useState(itemName.trim());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const search = useQuery({
    queryKey: ['quotes', 'item-prices', debounced],
    queryFn: () => searchQuoteItemPrices(debounced, 30),
    enabled: debounced.length > 0,
    staleTime: 60_000,
  });
  const rows = search.data ?? [];
  return (
    <div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 품목명 · 상세내용 (띄어쓰기로 여러 글자 · 예: HDD 16TB)"
        className="ui-input w-full mb-2" />
      {!debounced ? (
        <div className="px-3 py-8 text-sm text-slate-400 text-center">품목명을 적으면 지난 견적서에서 같은 품목을 찾습니다</div>
      ) : search.isPending ? (
        <div className="px-3 py-8 text-sm text-slate-400 text-center">찾는 중…</div>
      ) : search.isError ? (
        <div className="px-3 py-8 text-sm text-red-600 text-center">{(search.error as Error).message}</div>
      ) : rows.length === 0 ? (
        <div className="px-3 py-8 text-sm text-slate-400 text-center">지난 견적에 없습니다 — 글자를 줄여 보세요(「HDD」처럼 짧게)</div>
      ) : (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-2 py-1.5 text-left w-24">견적일</th><th className="px-2 py-1.5 text-left">받는 곳 · 프로젝트</th><th className="px-2 py-1.5 text-left">품목 · 규격</th>
                <th className="px-2 py-1.5 text-right w-14">수량</th><th className="px-2 py-1.5 text-right w-28">단가</th><th className="px-2 py-1.5 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.quoteId}-${i}`} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs">
                    <div className="text-slate-800">{r.quoteDate}</div>
                    <div className="text-slate-400 font-mono">{r.quoteNo}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="text-slate-800 font-medium truncate max-w-[14rem]" title={r.recipientCompany}>{r.recipientCompany || '—'}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[14rem]" title={r.projectName}>{r.projectName}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="text-slate-800">{r.itemName}{r.quoteKind === '유지보수' && <span className="ml-1 text-[11px] px-1 py-0.5 rounded bg-sky-100 text-sky-700">유지보수 · 월 단가</span>}</div>
                    <div className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-2" title={r.description}>{r.description}</div>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.qty.toLocaleString('ko-KR')}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800">{fmtKrw(r.unitPrice)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button type="button" onClick={() => onApply({ unitPrice: r.unitPrice, costUnit: null, itemName: r.itemName, description: r.description })}
                      className="btn btn-success btn-sm">이 단가로</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-slate-500 mt-1.5">품목명이 「HDD」처럼 뭉뚱그려진 줄이 많아 단가 차이가 큽니다 — 규격·받는 곳·날짜를 함께 보고 고르세요. 단가 0 줄은 빼고 최근 30줄까지.</div>
    </div>
  );
}
